import { strings } from "@perceptron/constants";
import { formatString } from "@perceptron/utils/string";

import { TRACK_MAX_GAP_SECONDS } from "../../constants";
import type { PreparedSpatialSegment } from "../../components/overlay/types";
import type { TTimelineItem } from "../../types";
import { formatSeconds, toPresentationUs } from "../../utils/time";
import { predictionAtTime } from "../player/selection";
import type { FramePrediction, IntervalResult, TrackingActivity, TrackingInterval, TrackingStage } from "../contracts";
import { isRecoveryPending, type RecoveryActivity, type RecoveryIntervalState } from "../recovery/contracts";

export interface TrackingInspectorRow {
  trackId: string;
  label: string;
  status: "queued" | "processing" | "ready" | "rejected" | "error" | "ineligible" | "disabled";
  title: string;
  detail: string;
  interval?: string;
  intervalId?: string;
  anchorTimes?: readonly [number, number];
  method?: string;
  phase?: IntervalResult["phase"];
  stage?: TrackingStage;
  workerId?: number;
  warnings: string[];
  frame?: FramePrediction;
  /** Current interval PTS plus one known adjacent frame per side for boundary navigation. */
  frameTimes?: readonly number[];
  timings?: IntervalResult["timings"];
  recovery?: RecoveryIntervalState;
}

export interface TrackingInspectorSnapshot {
  currentTime: number;
  summary: string;
  counts: { total: number; ready: number; processing: number; pending: number; failed: number };
  rows: TrackingInspectorRow[];
  workerError?: string;
  busy: boolean;
  workerCount?: number;
  recovery?: RecoveryActivity;
}

export type TrackingInspectorSummary = Omit<TrackingInspectorSnapshot, "rows">;

interface IndexedTrack {
  trackId: string;
  label: string;
  hasTrackIdentity: boolean;
  gtTimes: readonly number[];
  intervals: readonly TrackingInterval[];
}

export interface TrackingInspectorIndex {
  /** Depth-first timeline order; annotation timestamps never reorder these rows. */
  tracks: readonly IndexedTrack[];
}

/** Build only when timeline items, prepared segments or interval definitions change. */
export function createTrackingInspectorIndex(
  items: readonly TTimelineItem[],
  segments: readonly PreparedSpatialSegment[],
  intervals: readonly TrackingInterval[],
): TrackingInspectorIndex {
  const byTrack = new Map<string, TrackingInterval[]>();
  for (const interval of intervals) {
    const list = byTrack.get(interval.trackId) ?? [];
    list.push(interval);
    byTrack.set(interval.trackId, list);
  }
  const boxes = new Map<string, PreparedSpatialSegment[]>();
  for (const segment of segments) {
    if (segment.type !== "point_box") continue;
    const id = segment.trackId ?? segment.id;
    const list = boxes.get(id) ?? [];
    list.push(segment);
    boxes.set(id, list);
  }
  const order = new Set<string>();
  const visit = (item: TTimelineItem) => {
    if (item.type === "collection") item.children.forEach(visit);
    else if (item.type === "track" || item.type === "point_box") order.add(item.id);
  };
  items.forEach(visit);
  for (const id of boxes.keys()) order.add(id);
  const tracks: IndexedTrack[] = [];
  for (const trackId of order) {
    const trackBoxes = boxes.get(trackId);
    if (!trackBoxes?.length) continue;
    tracks.push({
      trackId,
      label: trackBoxes[0]!.label ?? trackId,
      hasTrackIdentity: trackBoxes[0]!.trackId !== undefined,
      gtTimes: trackBoxes
        .map((box) => box.startTime)
        .filter(Number.isFinite)
        .sort((a, b) => a - b),
      intervals: (byTrack.get(trackId) ?? []).sort((a, b) => b.start.time - a.start.time),
    });
  }
  return { tracks };
}

const timestampsByResult = new WeakMap<
  IntervalResult,
  { previous?: IntervalResult; next?: IntervalResult; times: readonly number[] }
>();

function sharesDecodedAnchor(left: IntervalResult, right: IntervalResult): boolean {
  const last = left.frames.at(-1);
  const first = right.frames[0];
  return (
    left.interval.trackId === right.interval.trackId &&
    left.interval.end.id === right.interval.start.id &&
    left.interval.end.time === right.interval.start.time &&
    last !== undefined &&
    first !== undefined &&
    toPresentationUs(last.timestamp) === toPresentationUs(first.timestamp)
  );
}

function frameTimes(result: IntervalResult, previous?: IntervalResult, next?: IntervalResult): readonly number[] {
  if (previous && !sharesDecodedAnchor(previous, result)) previous = undefined;
  if (next && !sharesDecodedAnchor(result, next)) next = undefined;
  const cached = timestampsByResult.get(result);
  if (cached && cached.previous === previous && cached.next === next) return cached.times;

  const times = result.frames.map((frame) => frame.timestamp);
  const first = times[0];
  const last = times.at(-1);
  if (previous && first !== undefined) {
    for (let i = previous.frames.length - 2; i >= 0; i--) {
      const timestamp = previous.frames[i]!.timestamp;
      if (toPresentationUs(timestamp) < toPresentationUs(first)) {
        times.unshift(timestamp);
        break;
      }
    }
  }
  if (next && last !== undefined) {
    const after = next.frames.find((frame) => toPresentationUs(frame.timestamp) > toPresentationUs(last));
    if (after) times.push(after.timestamp);
  }
  // A single adjacent frame is enough: after seeking, the neighboring interval
  // owns the row and supplies its own navigation window. Never flatten the video.
  timestampsByResult.set(result, { previous, next, times });
  return times;
}

function upperBound(times: readonly number[], currentTime: number): number {
  let lo = 0;
  let hi = times.length;
  while (lo < hi) {
    const middle = (lo + hi) >>> 1;
    if (times[middle]! <= currentTime) lo = middle + 1;
    else hi = middle;
  }
  return lo;
}

const STAGES: Record<TrackingStage, string> = strings.trackingInspector.statusCopy.stages;

export interface TrackingInspectorInput {
  currentTime: number;
  intervals: readonly TrackingInterval[];
  results: ReadonlyMap<string, IntervalResult>;
  errors: ReadonlyMap<string, string>;
  activity: TrackingActivity;
}

function hasFinalResult(activity: TrackingActivity, result: IntervalResult | undefined): boolean {
  return !!result && activity.completed.has(result.interval.id) && (!result.phase || result.phase === "final");
}

/** Compact lifecycle counts do not scan raw annotations or build per-track diagnostics. */
export function buildTrackingInspectorSummary({
  currentTime,
  intervals,
  results,
  errors,
  activity,
}: TrackingInspectorInput): TrackingInspectorSummary {
  const counts = { total: intervals.length, ready: 0, processing: 0, pending: 0, failed: 0 };
  for (const interval of intervals) {
    if (errors.has(interval.id)) counts.failed++;
    else if (activity.active.has(interval.id)) counts.processing++;
    else if (hasFinalResult(activity, results.get(interval.id))) counts.ready++;
    else counts.pending++;
  }
  const workerError = activity.enabled ? (errors.get("worker") ?? activity.unavailableReason) : undefined;
  const recovery = activity.recovery;
  const recoveryPending = !!recovery?.enabled && [...recovery.intervals.values()].some(isRecoveryPending);
  const recoveryFailed =
    !!recovery?.enabled && [...recovery.intervals.values()].some((state) => state.status === "failed");
  const busy = counts.processing > 0 || recoveryPending;
  const summary = !activity.enabled
    ? strings.trackingInspector.statusCopy.summary.off
    : workerError
      ? strings.trackingInspector.statusCopy.summary.unavailable
      : busy
        ? counts.processing > 0
          ? strings.trackingInspector.processing
          : strings.trackingInspector.recovery.title
        : counts.total === 0
          ? strings.trackingInspector.statusCopy.summary.noEligibleIntervals
          : counts.pending > 0
            ? strings.trackingInspector.recovery.stages.queued
            : counts.failed > 0 || recoveryFailed
              ? strings.trackingInspector.statusCopy.summary.finishedWithErrors
              : strings.trackingInspector.statusCopy.summary.allProcessed;
  return { currentTime, summary, counts, workerError, busy, workerCount: activity.workerCount, recovery };
}

/** Inspector diagnostics use the overlay's frame lookup and actual visible boxes. */
export function buildTrackingInspectorSnapshot({
  index,
  visible,
  summary: suppliedSummary,
  ...input
}: TrackingInspectorInput & {
  index: TrackingInspectorIndex;
  visible: readonly PreparedSpatialSegment[];
  /** Reuse the summary already computed for this presentation frame. */
  summary?: TrackingInspectorSummary;
}): TrackingInspectorSnapshot {
  const summary = suppliedSummary ?? buildTrackingInspectorSummary(input);
  const { currentTime, results, errors, activity } = input;
  const shownByTrack = new Map(
    visible
      .filter((segment) => segment.type === "point_box")
      .map((segment) => [segment.trackId ?? segment.id, segment] as const),
  );
  const rows: TrackingInspectorSnapshot["rows"] = [];
  for (const { trackId, label, hasTrackIdentity, gtTimes, intervals } of index.tracks) {
    const shown = shownByTrack.get(trackId);
    const method = shown?.type === "point_box" ? (shown.trackingMethod ?? "anchor") : undefined;
    const display =
      method === "anchor"
        ? strings.trackingInspector.statusCopy.display.gtVisible
        : method
          ? formatString(strings.trackingInspector.statusCopy.display.showing, method)
          : strings.trackingInspector.statusCopy.display.noBox;
    const base = { trackId, label, method, warnings: [] as string[] };
    if (!activity.enabled) {
      rows.push({
        ...base,
        status: "disabled",
        title: strings.trackingInspector.statusCopy.titles.interpolationOff,
        detail: formatString(strings.trackingInspector.statusCopy.details.disabled, display),
      });
      continue;
    }

    // Decoded anchor frames can begin before their GT timestamps. A newer raw
    // GT outside an old interval prevents that interval from owning this frame.
    const nextGT = upperBound(gtTimes, currentTime);
    const latestGT = gtTimes[nextGT - 1] ?? -Infinity;
    const intervalIndex = intervals.findIndex((candidate) => {
      if (latestGT > candidate.end.time) return false;
      const result = results.get(candidate.id);
      return (
        (currentTime >= candidate.start.time && currentTime <= candidate.end.time) ||
        (result !== undefined && predictionAtTime(result.frames, currentTime) !== undefined)
      );
    });
    const interval = intervals[intervalIndex];
    if (!interval) {
      const previous = gtTimes[nextGT - 1];
      const next = gtTimes[nextGT];
      const reason = !hasTrackIdentity
        ? strings.trackingInspector.statusCopy.reasons.noTrackIdentity
        : gtTimes.length < 2
          ? strings.trackingInspector.statusCopy.reasons.tooFewGT
          : previous === undefined
            ? strings.trackingInspector.statusCopy.reasons.beforeFirstGT
            : next === undefined
              ? strings.trackingInspector.statusCopy.reasons.afterLastGT
              : formatString(
                  strings.trackingInspector.statusCopy.reasons.neighboringGap,
                  formatSeconds(next - previous),
                  TRACK_MAX_GAP_SECONDS === 1
                    ? strings.trackingInspector.statusCopy.reasons.oneSecond
                    : formatString(strings.trackingInspector.statusCopy.reasons.multipleSeconds, TRACK_MAX_GAP_SECONDS),
                );
      rows.push({
        ...base,
        status: "ineligible",
        title: strings.trackingInspector.statusCopy.titles.noEligiblePair,
        detail: formatString(strings.trackingInspector.statusCopy.details.noEligiblePair, display, reason),
      });
      continue;
    }

    const result = results.get(interval.id);
    const active = activity.active.get(interval.id);
    const prediction = result && predictionAtTime(result.frames, currentTime);
    // The metadata index is newest-first. Only immediate GT neighbors can
    // contribute navigation frames; missing results must never be skipped.
    const previous = intervals[intervalIndex + 1];
    const next = intervals[intervalIndex - 1];
    const common = {
      ...base,
      interval: formatString(
        strings.trackingInspector.statusCopy.interval,
        formatSeconds(interval.start.time),
        formatSeconds(interval.end.time),
      ),
      intervalId: interval.id,
      anchorTimes: [interval.start.time, interval.end.time] as const,
      frame: prediction,
      frameTimes: result
        ? frameTimes(result, previous && results.get(previous.id), next && results.get(next.id))
        : undefined,
      timings: result?.timings,
      warnings: result?.warnings ?? [],
      phase: result?.phase,
      stage: active?.stage,
      workerId: active?.workerId,
      recovery: activity.recovery?.intervals.get(interval.id),
    };
    const error = errors.get(interval.id) ?? summary.workerError;
    if (error) {
      rows.push({
        ...common,
        status: "error",
        title: strings.trackingInspector.failed,
        detail: formatString(strings.trackingInspector.statusCopy.details.error, error, display),
      });
    } else if (active) {
      rows.push({
        ...common,
        status: "processing",
        title: STAGES[active.stage],
        detail:
          method === "sparse"
            ? strings.trackingInspector.sparsePreview
            : formatString(strings.trackingInspector.statusCopy.details.processing, display),
      });
    } else if (!hasFinalResult(activity, result)) {
      rows.push({
        ...common,
        status: "queued",
        title: strings.trackingInspector.recovery.stages.queued,
        detail: formatString(strings.trackingInspector.statusCopy.details.queued, display),
      });
    } else if (method) {
      rows.push({
        ...common,
        status: "ready",
        title:
          method === "anchor"
            ? strings.trackingInspector.statusCopy.titles.gtAnchor
            : formatString(
                strings.trackingInspector.statusCopy.titles.ready,
                method === "model-recovery" ? strings.trackingInspector.sources["model-recovery"] : method,
              ),
        detail:
          method === "anchor"
            ? strings.trackingInspector.statusCopy.details.anchor
            : method === "model-recovery"
              ? strings.trackingInspector.recovery.directDetail
              : formatString(
                  strings.trackingInspector.statusCopy.details.confidence,
                  Math.round((prediction?.confidence ?? 0) * 100),
                  prediction?.recovery
                    ? formatString(
                        strings.trackingInspector.statusCopy.details.recoverySuffix,
                        strings.trackingInspector.recovery.assisted,
                      )
                    : "",
                ),
      });
    } else if (isRecoveryPending(common.recovery)) {
      const recovery = common.recovery!;
      rows.push({
        ...common,
        status: recovery.status === "queued" ? "queued" : "processing",
        title: strings.trackingInspector.recovery.stages[recovery.status],
        detail:
          recovery.status === "refining"
            ? strings.trackingInspector.recovery.refiningDetail
            : recovery.status === "queued"
              ? strings.trackingInspector.recovery.queuedDetail
              : strings.trackingInspector.recovery.requestingDetail,
      });
    } else if (prediction?.method === "unavailable") {
      const confidence = [
        prediction.sparse &&
          formatString(
            strings.trackingInspector.statusCopy.confidence.sparse,
            Math.round(prediction.sparse.confidence * 100),
          ),
        prediction.nano &&
          formatString(
            strings.trackingInspector.statusCopy.confidence.nano,
            Math.round(prediction.nano.confidence * 100),
          ),
      ]
        .filter(Boolean)
        .join(" · ");
      rows.push({
        ...common,
        status: "rejected",
        title: strings.trackingInspector.statusCopy.titles.noTrustedBox,
        detail:
          common.recovery?.status === "failed"
            ? (common.recovery.error ?? strings.trackingInspector.recovery.stages.failed)
            : common.recovery?.status === "no_match"
              ? strings.trackingInspector.recovery.noMatchDetail
              : common.recovery?.status === "budget_exhausted"
                ? strings.trackingInspector.recovery.budgetDetail
                : common.recovery?.status === "recovered"
                  ? strings.trackingInspector.recovery.incompleteDetail
                  : formatString(
                      strings.trackingInspector.statusCopy.details.rejected,
                      confidence
                        ? formatString(strings.trackingInspector.statusCopy.details.confidenceSuffix, confidence)
                        : "",
                    ),
      });
    } else {
      rows.push({
        ...common,
        status: "error",
        title: strings.trackingInspector.statusCopy.titles.noDecodedFrame,
        detail: strings.trackingInspector.statusCopy.details.noDecodedFrame,
      });
    }
  }
  return { ...summary, rows };
}
